import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const BudgetCalculator = () => {
    const { t } = useTranslation();
    const [totalIncome, setTotalIncome] = useState(0);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [wagesAfterDeductions, setWagesAfterDeductions] = useState<number>();
    const [pensions, setPensions] = useState<number>();
    const [benefits, setBenefits] = useState<number>();
    const [otherIncome, setOtherIncome] = useState<number>();
    const [utilityBills, setUtilityBills] = useState<number>();
    const [rentMortgage, setRentMortgage] = useState<number>();
    const [loansCredit, setLoansCredit] = useState<number>();
    const [otherExpenses, setOtherExpenses] = useState<number>();
    const wagesAfterDeductionsInputRef = useRef<HTMLInputElement>(null);
    const pensionsInputRef = useRef<HTMLInputElement>(null);
    const benefitsInputRef = useRef<HTMLInputElement>(null);
    const otherIncomeInputRef = useRef<HTMLInputElement>(null);
    const rentMortgageInputRef = useRef<HTMLInputElement>(null);
    const utilityBillsInputRef = useRef<HTMLInputElement>(null);
    const loansCreditInputRef = useRef<HTMLInputElement>(null);
    const otherExpensesInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let total = 0;
        if (wagesAfterDeductions) total += wagesAfterDeductions;
        if (pensions) total += pensions;
        if (benefits) total += benefits;
        if (otherIncome) total += otherIncome;
        setTotalIncome(total);
    }, [wagesAfterDeductions, pensions, benefits, otherIncome]);

    useEffect(() => {
        let total = 0;
        if (rentMortgage) total += rentMortgage;
        if (utilityBills) total += utilityBills;
        if (loansCredit) total += loansCredit;
        if (otherExpenses) total += otherExpenses;
        setTotalExpenses(total);
    }, [rentMortgage, utilityBills, loansCredit, otherExpenses]);

    return <div className="bg-[#111a30] p-[30px] rounded-[25px] mt-[30px]">
        <h1 className="text-[20px] font-bold text-white mb-[25px]">{t('budget.title')}</h1>
        <div className="md:flex items-center gap-[6rem]">
            <div className="flex-1">
                <h1 className="text-[18px] font-bold text-white mb-[25px]">{t('budget.income')}</h1>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.wagesAfterDeductions')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {wagesAfterDeductions || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => wagesAfterDeductionsInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={wagesAfterDeductionsInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={wagesAfterDeductions}
                            onChange={(e) => setWagesAfterDeductions(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.pensions')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {pensions || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => pensionsInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={pensionsInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={pensions}
                            onChange={(e) => setPensions(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.benefits')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {benefits || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => benefitsInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={benefitsInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={benefits}
                            onChange={(e) => setBenefits(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.otherIncome')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {otherIncome || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => otherIncomeInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={otherIncomeInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={otherIncome}
                            onChange={(e) => setOtherIncome(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.totalIncome')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {totalIncome}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]">
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            disabled
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={totalIncome}
                        />
                    </div>
                </div>
            </div>
            <div className="flex-1 mt-[40px] md:mt-[0px]">
                <h1 className="text-[18px] font-bold text-white mb-[25px]">{t('budget.expenses')}</h1>
                <div className="flex flex-col ">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.rentMortgage')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {rentMortgage || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => rentMortgageInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={rentMortgageInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={rentMortgage}
                            onChange={(e) => setRentMortgage(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.utilityBills')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {utilityBills || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => utilityBillsInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={utilityBillsInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={utilityBills}
                            onChange={(e) => setUtilityBills(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.loansCredit')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {loansCredit || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => loansCreditInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={loansCreditInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={loansCredit}
                            onChange={(e) => setLoansCredit(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.otherExpenses')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {otherExpenses || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]" onClick={() => otherExpensesInputRef.current?.focus()}>
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            ref={otherExpensesInputRef}
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={otherExpenses}
                            onChange={(e) => setOtherExpenses(e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="flex flex-col mt-[20px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <p className="text-[18px] font-bold text-[#A2A2A2] capitalize">{t('budget.totalExpenses')}</p>
                        <p className="text-[18px] font-bold text-[#A2A2A2]">$ {totalExpenses || 0}</p>
                    </div>
                    <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] flex items-center gap-[10px] cursor-text border-[1px] border-[#B3BEC1]">
                        <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                        <input
                            disabled
                            className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            placeholder="0"
                            value={totalExpenses}
                        />
                    </div>
                </div>
            </div>
        </div>
        <div className="bg-[#272727] py-[14px] px-[20px] rounded-[10px] mt-[40px] border-[1px] border-[#B3BEC1]">
            <p className="text-[18px] font-bold text-[#A2A2A2] capitalize mb-[20px]">{t('budget.disposableIncome')}</p>
            <div className="flex items-center gap-[10px] ">
                <img className="w-[30px] h-[30px]" src={require("../../assets/images/flag.png")} alt="" />
                <input
                    className=" text-[20px] bg-transparent text-white w-full border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    type="number"
                    placeholder="0"
                    value={(totalIncome - totalExpenses) || 0}
                />
            </div>
        </div>
    </div>
}

export default BudgetCalculator;